<template>
  <v-col
    ref="extremeEvents"
    cols="12"
  >
    <v-row>
      <v-col
        v-if="coreEventsFirstItem"
        class="px-0"
        cols="12"
        lg="6"
        md="6"
      >
        <block-item-card
          :base-currency-symbol="baseCurrency ? baseCurrency.displaySymbol : ''"
          :export-element="elementExtremeEvents"
          :export-file-name="$t('NII.Tab.SynthesisRisk.Tab.RiskMethod1ExtremeEvent.Title')"
          :min-height="150"
          :title="coreEventsFirstItem.Name"
          :title-large="false"
          :title-value="coreEventsFirstItem.PortfolioLoss"
          :title-value-color-green="coreEventsFirstItem.PortfolioChangePositive"
          :title-value-color-red="coreEventsFirstItem.PortfolioChangeNegative"
          :tooltip="$t('NII.Tab.SynthesisRisk.Tab.RiskMethod1ExtremeEvent.Tooltip')"
          @export-to-image="onExportToImageClick"
          @export-to-image-completed="onExportToImageCompleted"
        >
          <v-lazy
            :options="{threshold: .5}"
            :value="disableLazy"
            transition="fade-transition"
          >
            <base-e-chart-line-area
              :axis-pointer-line-style-color="themeColorRGBA('synthesis-ui-blue-06')"
              :axis-pointer-line-style-width="2"
              :axis-x-max-value="coreEventsFirstItem.EndDate"
              :axis-x-min-value="coreEventsFirstItem.StartDate"
              :axis-x-split-number="2"
              :axis-y-label-inside="false"
              :axis-y-label-margin="5"
              :axis-y-tick-prefix="baseCurrency ? baseCurrency.displaySymbol : ''"
              :chart-animation="chartAnimation"
              :chart-animation-duration="600"
              :chart-data-options="coreEventsFirstItem.ChartData"
              :chart-grid-margin="{left: 20,top: 10,right: 40,bottom: 20,containLabel: true}"
              :chart-height="280"
              :chart-scale="true"
              :axis-y-min-value="getMinCoreEventYAxisValue"
              :axis-y-max-value="getMaxCoreEventYAxisValue"
              :axis-y-interval="getIntervalCoreEventYAxisValue"
            />
          </v-lazy>

          <v-card-text
            v-if="coreEventsFirstItem.PerAssetRates && coreEventsFirstItem.PerAssetRates.length"
            class="pt-0 pb-2 overflow-y-auto"
            style="max-height: 220px"
          >
            <template
              v-for="(asset, k) in coreEventsFirstItem.PerAssetRates"
            >
              <item-chart-legend
                :key="`extreme-events-per-asset-legend-first-${k}`"
                :background-color="themeColorRGBA(asset.ColorBg)"
                :title="asset.Asset"
                :title-value="asset.PercentageChange"
                :title-value-color="asset.Color"
              />
            </template>
          </v-card-text>
        </block-item-card>
      </v-col>

      <v-col
        v-if="extraEventsFirstItem"
        cols="12"
        lg="6"
        md="6"
      >
        <block-item-card
          :base-currency-symbol="baseCurrency ? baseCurrency.displaySymbol : ''"
          :min-height="150"
          :title="extraEventsFirstItem.Name"
          :title-large="false"
          :title-value="extraEventsFirstItem.PortfolioLoss"
          :title-value-color-green="extraEventsFirstItem.PortfolioChangePositive"
          :title-value-color-red="extraEventsFirstItem.PortfolioChangeNegative"
        >
          <template #title>
            <extreme-event-select
              :key="`extra-event-select-first`"
              v-model="extremeEventSelected[0]"
              :disabled="disabledExtraEventsSelect"
              :items="allSupportedEvents"
              :name="extraEventsFirstItem.Name"
            />
          </template>

          <v-lazy
            :options="{threshold: .5}"
            :value="disableLazy"
            transition="fade-transition"
          >
            <base-e-chart-line-area
              :axis-pointer-line-style-color="themeColorRGBA('synthesis-ui-blue-06')"
              :axis-pointer-line-style-width="2"
              :axis-x-max-value="extraEventsFirstItem.EndDate"
              :axis-x-min-value="extraEventsFirstItem.StartDate"
              :axis-x-split-number="2"
              :axis-y-label-inside="false"
              :axis-y-label-margin="5"
              :axis-y-tick-prefix="baseCurrency ? baseCurrency.displaySymbol : ''"
              :chart-animation="chartAnimation"
              :chart-animation-duration="600"
              :chart-data-options="extraEventsFirstItem.ChartData"
              :chart-grid-margin="{left: 20,top: 10,right: 40,bottom: 20,containLabel: true}"
              :chart-height="280"
              :axis-y-min-value="getMinExtraEventYAxisValue"
              :axis-y-max-value="getMaxExtraEventYAxisValue"
              :axis-y-interval="getIntervalExtraEventYAxisValue"
            />
          </v-lazy>

          <v-card-text
            v-if="extraEventsFirstItem.PerAssetRates && extraEventsFirstItem.PerAssetRates.length"
            class="pt-0 pb-2 overflow-y-auto"
            style="max-height: 220px"
          >
            <template
              v-for="(asset, k) in extraEventsFirstItem.PerAssetRates"
            >
              <item-chart-legend
                :key="`extra-events-per-asset-legend-${k}`"
                :background-color="themeColorRGBA(asset.ColorBg)"
                :title="asset.Asset"
                :title-value="asset.PercentageChange"
                :title-value-color="asset.Color"
              />
            </template>
          </v-card-text>
        </block-item-card>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import chartsColorMixin              from '@/theme/default/components/charts/mixins/chartsColorMixin'
import BaseEChartLineArea            from '@/theme/default/components/charts/echarts/BaseEChartLineArea'
import API                           from '@/api/Api'
import { clone, computeYAxisBounds }                     from '@/lib/utils/helper'
import BlockItemCard                 from '@/theme/default/components/common/BlockItemCard'
import ItemChartLegend               from '@/theme/default/components/common/ItemChartLegend'
import NIIExtremeEventsBaseResource from '@/api/resources/risks/NIIExtremeEventsBaseResource'
import ExtremeEventSelect            from '@/theme/default/components/common/ExtremeEventSelect'
import { first }                     from '@/lib/utils/array'

export default {
  name      : 'ExtremeEvents',
  components: {
    ExtremeEventSelect,
    ItemChartLegend,
    BlockItemCard,
    BaseEChartLineArea
  },
  directives: {},
  mixins    : [chartsColorMixin],
  props     : {},
  dataStore : {
    appToolbarExtended  : 'App.Config.AppToolbarExtended',
    niiExtremeEventsData: 'NII.FindExtremeEventsData',
    niiBaseCurrencyData : 'NII.FindSummaryData.Data',
    selectedPeriodStore : 'NII.SelectedPeriod',
    currentAnalysisId   : 'ActiveAnalysis.currentAnalysisId'
  },
  enums    : {},
  dataModel: null,
  data () {
    return {
      onInit                    : true,
      elementExtremeEvents      : null,
      hideExtraEventsSelect     : false,
      disableLazy               : false,
      chartAnimation            : true,
      extremeEventSelected      : [],
      extremeEventSelectedPeriod: null,
      selectedPeriod            : null,
      originalApiData           : null,
      niiAnalysisId             : null
    }
  },
  computed: {
    buttonPeriodWidth () {
      return this.$vuetify.breakpoint.mdAndUp ? (this.appToolbarExtended ? 200 : (this.$vuetify.breakpoint.xl ? 290 : 210)) : 150
    },

    extremeEvents () {
      return this.niiExtremeEventsData?.Data || null
    },

    events () {
      return this.extremeEvents?.Events || null
    },

    coreEvents () {
      return this.extremeEvents?.CoreEvents || null
    },

    coreEventsFirstItem () {
      if (this.coreEvents && !!this.coreEvents.length) {
        return first(this.coreEvents)
      }

      return null
    },

    getMinCoreEventYAxisValue () {
      return computeYAxisBounds(this.coreEventsFirstItem?.ChartData[0]?.y || []).min || 0
    },
    getMaxCoreEventYAxisValue () {
      return computeYAxisBounds(this.coreEventsFirstItem?.ChartData[0]?.y || []).max || 0
    },
    getIntervalCoreEventYAxisValue () {
      return computeYAxisBounds(this.coreEventsFirstItem?.ChartData[0]?.y || []).interval || 0
    },

    extraEventsFirstItem () {
      if (this.extraEvents && !!this.extraEvents.length) {
        return first(this.extraEvents)
      } else {
        return (this.events && !!this.events.length ? this.events[1] : null)
      }
    },

    getMinExtraEventYAxisValue () {
      return computeYAxisBounds(this.extraEventsFirstItem?.ChartData[0]?.y || []).min || 0
    },
    getMaxExtraEventYAxisValue () {
      return computeYAxisBounds(this.extraEventsFirstItem?.ChartData[0]?.y || []).max || 0
    },
    getIntervalExtraEventYAxisValue () {
      return computeYAxisBounds(this.extraEventsFirstItem?.ChartData[0]?.y || []).interval || 0
    },

    extraEvents () {
      return this.extremeEvents?.ExtraEvents || null
    },

    allSupportedEvents () {
      return this.extremeEvents?.AllSupportedEvents || null
    },

    disabledExtraEventsSelect () {
      if (this.hideExtraEventsSelect) {
        return true
      }

      return this.extremeEvents?.Disabled || false
    },
    accountId () {
      return this.user?.selectedAccountId || null
    },

    baseCurrency () {
      return this.niiBaseCurrencyData?.NIIBaseCurrency || null
    }
  },
  watch: {
    extremeEventSelected () {
      if (this.onInit) {
        this.onInit = false
        return
      }
      this.updateExtremeEvents()
    }
  },
  beforeCreate () {},
  created () {
    this.getData()
    this.$bus.$on('nii:period-changed', this.onPeriodChanged)
    this.$bus.$on('nii:cancel-assumptions-edit', this.getData)
  },
  beforeDestroy () {
    this.$bus.$off('nii:period-changed', this.onPeriodChanged)
    this.$bus.$off('nii:cancel-assumptions-edit', this.getData)
  },
  beforeMount () {},
  mounted () {
    this.$nextTick(() => {
      this.elementExtremeEvents = this.$el
    })
  },
  beforeUpdate () {},
  updated () {},
  destroyed () {},
  methods: {
    updateExtremeEvents () {
      API.Resource.NII.UpdateExtremeEvents({
        niiAnalysisId : this.niiAnalysisId,
        selectedEvents: this.extremeEventSelected,
        riskType      : this.selectedPeriodStore === 'risk12mAnalysisData' ? 'Risk12m' : 'Risk3m'
      })
        .then(response => {
          this.handleUpdateExtremeEventsResponse(response)
        })
        .catch(e => {
          this.handleUpdateExtremeEventsResponse(e.response)
        })
    },
    handleUpdateExtremeEventsResponse (response) {
      const data = API.responseData(response) || null
      const errors = API.responseErrors(response) || []
      const result = data?.selectNIIExtremeEvents || null

      if (API.isResponseSuccess(response) && result) {
        const o = clone(result)
        o.selectedPeriod = this.selectedPeriodStore

        const v = new NIIExtremeEventsBaseResource(o)

        this.$set(this.niiExtremeEventsData, 'Data', v)
      } else {
        this.$bus.$emit('app:error', errors)
      }
    },

    getData () {
      API.Resource.NII.FindExtremeEvents(this.accountId, this.currentAnalysisId)
        .then(response => {
          this.handleResponse(response)
        })
        .catch(e => {
          this.handleResponse(e.response)
        })
    },
    handleResponse (response) {
      const data = API.responseData(response) || null
      const errors = API.responseErrors(response) || []
      const result = data?.findNiiAnalysis || null

      if (API.isResponseSuccess(response) && result) {
        // Store the original API data for period switching
        this.originalApiData = clone(result)

        const o = clone(result)
        o.selectedPeriod = this.selectedPeriodStore
        const v = new NIIExtremeEventsBaseResource(o)

        if (v) {
          if (v.SelectedEvents && !!v.SelectedEvents.length) {
            const selected = first(v.SelectedEvents)

            if (selected) {
              this.extremeEventSelected = [
                selected.Value
              ]
            }
          }

          if (v.SelectedPeriod) {
            this.extremeEventSelectedPeriod = v.SelectedPeriod
          }

          if (v.NIIAnalysisId) {
            this.niiAnalysisId = v.NIIAnalysisId
          }
        }

        this.$set(this.niiExtremeEventsData, 'Data', v)
      } else {
        this.$bus.$emit('app:error', errors)
      }
    },

    onExportToImageClick () {
      this.chartAnimation = false
      this.disableLazy = true
      this.hideExtraEventsSelect = true
    },

    onExportToImageCompleted () {
      this.hideExtraEventsSelect = false
    },

    onPeriodChanged (newPeriod) {
      // If we have selected events, create a new query for the selected event with the new period
      if (this.extremeEventSelected && this.extremeEventSelected.length > 0 && this.niiAnalysisId) {
        this.updateExtremeEventsForNewPeriod(newPeriod)
      } else {
        // Fallback to original behavior if no selected events
        this.recreateResourceWithNewPeriod(newPeriod)
      }

      // If there's a selected period, retrigger CalculateExtremePeriod with the new period
      if (this.extremeEventSelectedPeriod && this.niiAnalysisId) {
        this.retriggerCalculateExtremePeriod(newPeriod)
      }
    },

    updateExtremeEventsForNewPeriod (newPeriod) {
      // Create a new query for the selected event with the new period
      API.Resource.NII.UpdateExtremeEvents({
        niiAnalysisId : this.niiAnalysisId,
        selectedEvents: this.extremeEventSelected,
        riskType      : newPeriod === 'risk12mAnalysisData' ? 'Risk12m' : 'Risk3m'
      })
        .then(response => {
          this.handleUpdateExtremeEventsForPeriodResponse(response, newPeriod)
        })
        .catch(e => {
          this.handleUpdateExtremeEventsForPeriodResponse(e.response, newPeriod)
        })
    },

    handleUpdateExtremeEventsForPeriodResponse (response, newPeriod) {
      const data = API.responseData(response) || null
      const errors = API.responseErrors(response) || []
      const result = data?.selectNIIExtremeEvents || null

      if (API.isResponseSuccess(response) && result) {
        // Update the original API data with the new period data
        if (this.originalApiData) {
          const updatedData = clone(this.originalApiData)
          updatedData[newPeriod] = {
            ...updatedData[newPeriod],
            extremeEvents: result
          }
          this.originalApiData = updatedData
        }

        // Create new resource with updated data
        const o = clone(this.originalApiData)
        o.selectedPeriod = newPeriod
        const v = new NIIExtremeEventsBaseResource(o)

        // Update the correct key with the new data
        this.$set(this.niiExtremeEventsData, 'Data', v)
      } else {
        this.$bus.$emit('app:error', errors)
        // Fallback to original behavior on error
        this.recreateResourceWithNewPeriod(newPeriod)
      }
    },

    recreateResourceWithNewPeriod (newPeriod) {
      // Recreate the resource with the new selectedPeriod from original API data
      if (this.originalApiData) {
        const o = clone(this.originalApiData)
        o.selectedPeriod = newPeriod
        const v = new NIIExtremeEventsBaseResource(o)
        this.$set(this.niiExtremeEventsData, 'Data', v)
      }
    },

    retriggerCalculateExtremePeriod (newPeriod) {
      // Retrigger CalculateExtremePeriod with the current selected period and new risk type
      if (this.selectedPeriod && this.selectedPeriod.Value && this.niiAnalysisId) {
        const period = this.selectedPeriod.Value // Use the stored period value
        const riskType = newPeriod === 'risk12mAnalysisData' ? 'Risk12m' : 'Risk3m'

        API.Resource.NII.CalculateExtremePeriod(this.niiAnalysisId, period, riskType)
          .then(response => {
            this.handleUpdateExtremeEventResponse(response)
          })
          .catch(e => {
            this.handleUpdateExtremeEventResponse(e.response)
          })
      }
    }
  }
}
</script>

<style scoped>

</style>
